import type { User } from '@/types/user'
import { createResourceId } from './create-resource-id'
import { apiConfig } from '@/config'
import { wait } from '@/utils/wait'
import { users } from './data'
import {
  callAccessRecovery,
  callAccountAttest,
  callAccountLogin,
  callAccountLostAccess,
  callAccountMe,
  LoginParameter,
  RecoveryParameter,
  UserProfile,
} from '../index'
import {
  callImpersonationEnd,
  callImpersonationExchange,
} from '../impersonation'

const STORAGE_KEY = 'users'

// NOTE: We use localStorage since memory storage is lost after page reload.
//  This should be replaced with a server call that returns DB persisted data.

const getPersistedUsers = (): User[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEY)

    if (!data) {
      return []
    }

    return JSON.parse(data) as User[]
  } catch (err) {
    console.error(err)
    return []
  }
}

const persistUser = (user: User): void => {
  try {
    const users = getPersistedUsers()
    const data = JSON.stringify([...users, user])
    localStorage.setItem(STORAGE_KEY, data)
  } catch (err) {
    console.error(err)
  }
}

type SignInRequest = {
  email: string
  password: string
}

type SignInResponse = Promise<{
  accessToken: string
}>

type SignUpRequest = {
  email: string
  firstName: string
  lastName: string
  password: string
}

type LostAccessRequest = {
  email: string
}

type RenewPasswordRequest = {
  login: LoginParameter
  previousPassword: string | null
  tid: string | undefined
  token: string | undefined
}

type SignUpResponse = Promise<{
  accessToken: string
}>

type MeRequest = {
  accessToken: string
}

type MeResponse = Promise<UserProfile>

class AuthApi {
  private getBaseUrl(): string {
    return apiConfig().apiUrl.replace(/\/api$/, '')
  }

  async signIn(request: SignInRequest): Promise<SignInResponse> {
    try {
      const signInResponse = await callAccountLogin(request, {
        baseUrl: this.getBaseUrl(),
      })

      const authorizationHeader =
        signInResponse.headers?.get('Authorization') ?? ''
      const accessToken = authorizationHeader.split(' ')[1]

      if (!accessToken || accessToken.split('.').length !== 3) {
        throw new Error(
          'Missing or invalid access token in Authorization response header'
        )
      }

      return { accessToken }
    } catch (err) {
      console.error('[AuthApi:signIn]', err)
      throw new Error('Internal server error')
    }
  }

  /** Code passé par la console : route publique, comme la connexion, donc hors du préfixe `/api`. */
  async exchangeImpersonationCode(code: string): Promise<SignInResponse> {
    try {
      const exchangeResponse = await callImpersonationExchange(code, {
        baseUrl: this.getBaseUrl(),
      })

      if (exchangeResponse.status !== 200) {
        throw new Error('Impersonation code refused')
      }

      const authorizationHeader =
        exchangeResponse.headers?.get('Authorization') ?? ''
      const accessToken = authorizationHeader.split(' ')[1]

      if (!accessToken || accessToken.split('.').length !== 3) {
        throw new Error(
          'Missing or invalid access token in Authorization response header'
        )
      }

      return { accessToken }
    } catch (err) {
      console.error('[AuthApi:exchangeImpersonationCode]', err)
      throw new Error('Internal server error')
    }
  }

  async endImpersonation(): Promise<void> {
    await callImpersonationEnd()
  }

  async lostAccess(request: LostAccessRequest): Promise<{ response: unknown }> {
    try {
      const response = await callAccountLostAccess(request, {
        baseUrl: this.getBaseUrl(),
      })

      return { response }
    } catch (err) {
      console.error('[AuthApi:lostAccess]', err)
      throw new Error('Internal server error')
    }
  }

  async renewPassword(
    request: RenewPasswordRequest
  ): Promise<{ response: unknown }> {
    try {
      const response = await callAccessRecovery(request, {
        baseUrl: this.getBaseUrl(),
      })

      return { response }
    } catch (err) {
      console.error('[AuthApi:renewPassword]', err)
      // Erreur d'origine gardée : la page lit son statut (401 = lien expiré, déjà utilisé ou remplacé).
      throw err
    }
  }

  async attest(request: RecoveryParameter): Promise<{ response: unknown }> {
    try {
      const response = await callAccountAttest(request, {
        baseUrl: this.getBaseUrl(),
      })

      return { response }
    } catch (err) {
      console.error('[AuthApi:attest]', err)
      // Erreur d'origine gardée : la page lit son statut (401 = lien expiré, déjà utilisé ou remplacé).
      throw err
    }
  }

  async signUp(request: SignUpRequest): Promise<SignUpResponse> {
    try {
      const { email, firstName, lastName, password } = request

      await wait(1000)

      const mergedUsers = [...users, ...getPersistedUsers()]
      const existingUser = mergedUsers.find((user) => user.email === email)

      if (existingUser) {
        throw new Error('User already exists')
      }

      const user = {
        id: createResourceId(),
        email,
        firstName,
        lastName,
        password,
      }

      persistUser(user)

      return { accessToken: 'accessToken' }
    } catch (err) {
      console.error('[AuthApi:signUp]', err)
      throw err instanceof Error ? err : new Error('Internal server error')
    }
  }

  async me(request: MeRequest): Promise<MeResponse> {
    try {
      const user = await callAccountMe({
        headers: {
          Authorization: request.accessToken,
        },
      })

      return user
    } catch (err) {
      console.error('[AuthApi:me]', err)
      throw new Error('Internal server error')
    }
  }
}

export const authApi = new AuthApi()
